"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const InsurancePage = () => {
    const { t, translationsLoaded } = useComponentTranslations('Insurance');
    const it = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: it('insuranceExpertise1'), icon: 'carbon:analytics' },
        { title: it('insuranceExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: it('insuranceExpertise3'), icon: 'carbon:rule' },
        { title: it('insuranceExpertise4'), icon: 'carbon:security' },
        { title: it('insuranceExpertise5'), icon: 'carbon:cloud-app' },
        { title: it('insuranceExpertise6'), icon: 'carbon:blockchain' },
        { title: it('insuranceExpertise7'), icon: 'carbon:data-refinery' },
        { title: it('insuranceExpertise8'), icon: 'carbon:chart-line-data' },
        { title: it('insuranceExpertise9'), icon: 'carbon:user-profile' },
    ];

    // Данные для аккордеона (Примеры решений)
    const useCasesForAccordion = [
        { title: it('insuranceUseCase1'), description: it('insuranceUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: it('insuranceUseCase2'), description: it('insuranceUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: it('insuranceUseCase3'), description: it('insuranceUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: it('insuranceUseCase4'), description: it('insuranceUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    // Данные для чередующегося макета (Услуги)
    const servicesForAlternating = [
        { title: it('insuranceService1Title'), description: it('insuranceService1Desc'), image: '/images/finance.jpg' },
        { title: it('insuranceService2Title'), description: it('insuranceService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: it('insuranceService3Title'), description: it('insuranceService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: it('insuranceService4Title'), description: it('insuranceService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: it('insuranceService5Title'), description: it('insuranceService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: it('insuranceFaq1q'), answer: it('insuranceFaq1a') },
        { question: it('insuranceFaq2q'), answer: it('insuranceFaq2a') },
        { question: it('insuranceFaq3q'), answer: it('insuranceFaq3a') },
        { question: it('insuranceFaq4q'), answer: it('insuranceFaq4a') },
        { question: it('insuranceFaq5q'), answer: it('insuranceFaq5a') },
    ];

    return (
        <div className="relative min-h-screen bg-transparent text-white">
            {/* Hero-блок */}
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {it('insuranceHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {it('insuranceHeroSubtitle')}
                </motion.p>
            </header>

            {/* Проблемы индустрии */}
            <section className="w-full py-24 bg-white/5 border-y border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{it('insurancePainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {it('insurancePainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Наше решение / Expertise */}
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{it('insuranceExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 border border-white/10 rounded-lg bg-white/5 flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={it('insuranceServicesTitle')} useCases={servicesForAlternating} />
            </div>

            {/* Accordion with Use Cases */}
            <ServicesAccordion title={it('insuranceUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={it('insuranceTechTitle')} t={t} />
                <IndustrialsForm label={it('insuranceCtaTitle')} />

                {/* FAQ */}
                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{it('insuranceFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 border border-white/10 rounded-lg bg-white/5">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>

            </div>
        </div>
    );
};

export default InsurancePage;
